/**
 * Shipment Detail Page — Customer Portal
 *
 * Shows full shipment details with timeline, documents, and container tracking.
 * This was a P0 gap — customer clicks led to 404.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Ship, Plane, Truck, MapPin, Calendar, Package, FileText, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/lib/api-base';

interface ShipmentDetail {
  id: string;
  jobNo: string;
  blNo: string;
  origin: string;
  destination: string;
  mode: string;
  status: string;
  vessel: string;
  voyage: string;
  etd: string;
  eta: string;
  containerNo: string;
  packages: number;
  weight: number;
  volume: number;
  customerRef: string;
  carrier: string;
}

interface ShipmentEvent {
  id: string;
  type: string;
  description: string;
  timestamp: string;
}

const STATUS_BADGE: Record<string, string> = {
  delivered:  'bg-emerald-100 text-emerald-700',
  in_transit: 'bg-blue-100 text-blue-700',
  booked:     'bg-slate-100 text-slate-600',
  customs:    'bg-amber-100 text-amber-700',
  closed:     'bg-green-100 text-green-700',
  draft:      'bg-gray-100 text-gray-600',
};

const MODE_ICON: Record<string, typeof Ship> = {
  'sea-import': Ship,
  'sea-export': Ship,
  ocean: Ship,
  sea: Ship,
  air: Plane,
  road: Truck,
};

const MODE_LABEL: Record<string, string> = {
  'sea-import': 'Sea Import',
  'sea-export': 'Sea Export',
  ocean: 'Ocean',
  air: 'Air',
  road: 'Road',
};

export function ShipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [events, setEvents] = useState<ShipmentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);

    // Fetch shipment detail
    const headers = { Authorization: `Bearer ${accessToken}` };
    Promise.all([
      fetch(`${API_BASE}/api/selfservice/shipments/${id}`, { headers })
        .then(r => r.ok ? r.json() : Promise.reject('Not found')),
      fetch(`${API_BASE}/api/selfservice/shipments/${id}/events`, { headers })
        .then(r => r.ok ? r.json() : { data: { events: [] } })
        .catch(() => ({ data: { events: [] } })),
    ])
      .then(([shipRes, evtRes]) => {
        const s = shipRes.data ?? shipRes;
        setShipment({
          id: String(s.id ?? ''),
          jobNo: String(s.jobNo ?? s.shipment_no ?? s.shipmentNo ?? ''),
          blNo: String(s.hblNo ?? s.master_bl_no ?? s.masterBlNo ?? ''),
          origin: String(s.pol ?? s.origin ?? ''),
          destination: String(s.pod ?? s.destination ?? ''),
          mode: String(s.mode ?? 'sea-import'),
          status: String(s.status ?? 'active').toLowerCase(),
          vessel: String(s.vessel ?? ''),
          voyage: String(s.voyage ?? ''),
          etd: s.etd ? String(s.etd).slice(0, 10) : '',
          eta: s.eta ? String(s.eta).slice(0, 10) : '',
          containerNo: String(s.containerNo ?? s.container_numbers ?? ''),
          packages: Number(s.total_packages ?? s.totalPackages ?? 0),
          weight: Number(s.total_weight ?? s.totalWeight ?? 0),
          volume: Number(s.total_volume ?? s.totalVolume ?? 0),
          customerRef: String(s.customer_ref ?? s.customerRef ?? ''),
          carrier: String(s.carrier_name ?? s.carrierName ?? ''),
        });
        setEvents(
          (evtRes.data?.events ?? []).map((e: Record<string, unknown>) => ({
            id: String(e.id ?? ''),
            type: String(e.event_type ?? e.eventType ?? ''),
            description: String(e.description ?? ''),
            timestamp: String(e.created_at ?? e.createdAt ?? '').slice(0, 16),
          }))
        );
      })
      .catch(() => setError('Unable to load shipment details'))
      .finally(() => setLoading(false));
  }, [id, accessToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-6 h-6 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="space-y-4 animate-fade-in">
        <button onClick={() => navigate('/portal/shipments')} className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800">
          <ArrowLeft className="w-4 h-4" /> Back to Shipments
        </button>
        <div className="text-center py-12 text-slate-500">{error ?? 'Shipment not found'}</div>
      </div>
    );
  }

  const ModeIcon = MODE_ICON[shipment.mode] ?? Ship;

  return (
    <div className="space-y-5 animate-fade-in max-w-4xl">
      {/* Back + Header */}
      <button onClick={() => navigate('/portal/shipments')} className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800">
        <ArrowLeft className="w-4 h-4" /> Back to Shipments
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ModeIcon className="w-5 h-5 text-brand-600" />
            {shipment.jobNo}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">B/L: {shipment.blNo || '—'} · {MODE_LABEL[shipment.mode] ?? shipment.mode}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[shipment.status] ?? 'bg-slate-100 text-slate-600'}`}>
          {shipment.status.replace('_', ' ')}
        </span>
      </div>

      {/* Route Card */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-500" /> Route
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Origin</div>
            <div className="font-medium text-slate-800">{shipment.origin || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Destination</div>
            <div className="font-medium text-slate-800">{shipment.destination || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">ETD</div>
            <div className="font-medium text-slate-800">{shipment.etd || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">ETA</div>
            <div className="font-medium text-slate-800">{shipment.eta || '—'}</div>
          </div>
        </div>
      </div>

      {/* Vessel & Carrier */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Ship className="w-4 h-4 text-brand-500" /> Vessel & Carrier
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Vessel</div>
            <div className="font-medium text-slate-800">{shipment.vessel || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Voyage</div>
            <div className="font-medium text-slate-800">{shipment.voyage || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Carrier</div>
            <div className="font-medium text-slate-800">{shipment.carrier || '—'}</div>
          </div>
          <div>
            <div className="text-slate-400 text-xs mb-0.5">Container</div>
            <div className="font-mono text-xs text-slate-800">{shipment.containerNo || '—'}</div>
          </div>
        </div>
      </div>

      {/* Cargo Summary */}
      {(shipment.packages > 0 || shipment.weight > 0) && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-500" /> Cargo
          </h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-slate-400 text-xs mb-0.5">Packages</div>
              <div className="font-medium text-slate-800">{shipment.packages.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs mb-0.5">Weight (kg)</div>
              <div className="font-medium text-slate-800">{shipment.weight.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs mb-0.5">Volume (CBM)</div>
              <div className="font-medium text-slate-800">{shipment.volume}</div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline / Events */}
      {events.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-brand-500" /> Timeline
          </h2>
          <div className="space-y-3">
            {events.map((ev, i) => (
              <div key={ev.id || i} className="flex items-start gap-3">
                <div className="mt-1.5 w-2 h-2 rounded-full bg-brand-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">{ev.description}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{ev.timestamp}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {events.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 text-center text-sm text-slate-400">
          No tracking events yet
        </div>
      )}
    </div>
  );
}
